/*
  Shared FEMALE / MALE gender state + <norse-gender-switch> selector.

  Single source of truth is `document.documentElement.dataset.norseGender`, so every
  switch on the page reflects the same state no matter which one was operated, and
  sections can follow along with a CSS attribute selector alone (see
  norse-gender-switch.css → [data-norse-gender-only]).

  Consumers that need logic (buy box form sync, gallery swap) use either:
    document.addEventListener(window.NorseGender.EVENT, (e) => e.detail.gender)
    window.NorseGender.current()

  The selector follows the ARIA radiogroup pattern: one tab stop for the group
  (roving tabindex), arrow keys move between and select options, Home/End jump.
*/
(function () {
  'use strict';

  const GENDERS = ['female', 'male'];
  const DEFAULT_GENDER = 'female';
  const EVENT = 'norse:gender-change';

  const normalize = (value) => {
    const candidate = String(value ?? '')
      .trim()
      .toLowerCase();
    return GENDERS.includes(candidate) ? candidate : null;
  };

  const current = () => normalize(document.documentElement.dataset.norseGender);

  const set = (value, { force = false } = {}) => {
    const gender = normalize(value);
    if (!gender) return;
    if (!force && current() === gender) return;

    document.documentElement.dataset.norseGender = gender;
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { gender }, bubbles: true }));
  };

  // FEMALE on every load, per spec. `?gender=male` is honoured so campaigns can
  // deep-link the male state — the one capability the two separate pages had.
  const requested = () => {
    const params = new URLSearchParams(window.location.search);
    return normalize(params.get('gender')) ?? DEFAULT_GENDER;
  };

  if (!window.NorseGender) {
    window.NorseGender = { GENDERS, DEFAULT_GENDER, EVENT, current, set, requested };
  }

  if (customElements.get('norse-gender-switch')) return;

  customElements.define(
    'norse-gender-switch',
    class NorseGenderSwitch extends HTMLElement {
      constructor() {
        super();
        this._onClick = this._onClick.bind(this);
        this._onKeydown = this._onKeydown.bind(this);
        this._onGenderChange = this._onGenderChange.bind(this);
      }

      connectedCallback() {
        this._options = [...this.querySelectorAll('[data-ngs-option]')];
        if (this._options.length === 0) return;

        this.addEventListener('click', this._onClick);
        this.addEventListener('keydown', this._onKeydown);
        document.addEventListener(EVENT, this._onGenderChange);

        // The first switch to connect resolves the page state; later ones adopt it.
        // Also covers Shopify section reloads, where state already exists on <html>.
        const gender = current();
        if (gender) {
          this._paint(gender);
        } else {
          set(requested(), { force: true });
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this._onClick);
        this.removeEventListener('keydown', this._onKeydown);
        document.removeEventListener(EVENT, this._onGenderChange);
      }

      _onClick(event) {
        const option = event.target.closest('[data-ngs-option]');
        if (option && this.contains(option)) set(option.dataset.gender);
      }

      _onKeydown(event) {
        const keys = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End'];
        if (!keys.includes(event.key)) return;

        const index = this._options.indexOf(document.activeElement);
        if (index === -1) return;
        event.preventDefault();

        let next;
        if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = this._options.length - 1;
        } else {
          const step = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
          next = (index + step + this._options.length) % this._options.length;
        }

        set(this._options[next].dataset.gender);
        this._options[next].focus();
      }

      _onGenderChange(event) {
        this._paint(event.detail?.gender);
      }

      _paint(value) {
        const gender = normalize(value);
        if (!gender) return;
        this._options.forEach((option) => {
          const isSelected = option.dataset.gender === gender;
          option.setAttribute('aria-checked', String(isSelected));
          option.tabIndex = isSelected ? 0 : -1;
        });
      }
    }
  );
})();
